// TB-107: patient search helpers shared by the landing, dashboard and search page.
import { inBahrain } from '../config/map'
import { personName, doctorTitle } from './displayName'

const TITLE_WORDS = new Set(['dr', 'dr.', 'doctor'])

/** "Dr. Saji", "dr saji" and " SAJI " all become "saji"; a bare "Dr" becomes ''. */
export function normalizeSearchTerm (raw) {
  if (typeof raw !== 'string') return ''
  return raw
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.trim())
    .filter((w) => w && !TITLE_WORDS.has(w))
    .map((w) => w.replace(/^dr\.(?=\S)/, ''))
    .filter(Boolean)
    .join(' ')
}

/**
 * Route to the results page. A blank query lists every doctor (the search
 * page treats a single space that way); the location rides in the query string.
 */
export function buildSearchRoute (query, location) {
  const q = normalizeSearchTerm(query)
  const loc = typeof location === 'string' ? location.trim() : ''
  const route = { name: 'search', params: { searchText: q || ' ' } }
  if (loc) route.query = { loc }
  return route
}

/** Every location of a result with valid Bahrain coordinates, as numbers. */
function bahrainLocations (item) {
  let list = []
  if (Array.isArray(item && item.location)) list = item.location
  else if (item && item.location && typeof item.location === 'object') list = [item.location]
  return list
    .map((loc) => ({
      loc,
      lat: Number(loc && (loc.latitude ?? loc.lat)),
      lng: Number(loc && (loc.longitude ?? loc.lng))
    }))
    .filter((p) => p.loc && inBahrain(p.lat, p.lng))
}

/**
 * Map pins for search results: one pin per doctor location inside Bahrain.
 * Doctors with no usable location are returned in `withoutLocation` so the
 * page can say so instead of hiding them. Establishments (no firstName) are
 * left out, as before.
 */
export function mapPinsFor (results) {
  const pins = []
  const withoutLocation = []
  const seen = new Set()
  ;(Array.isArray(results) ? results : []).forEach((item) => {
    if (!item || !item.id || (item.name !== undefined && item.firstName === undefined)) return
    if (seen.has(item.id)) return
    seen.add(item.id)
    const locs = bahrainLocations(item)
    if (!locs.length) {
      withoutLocation.push({ id: item.id, name: doctorTitle(personName(item)) || 'Doctor' })
      return
    }
    locs.forEach((p, i) => {
      pins.push({
        id: item.id,
        key: `${item.id}:${p.loc.id || i}`,
        position: { lat: p.lat, lng: p.lng },
        distance: typeof item.distance === 'number' ? item.distance : 0,
        info: item
      })
    })
  })
  return { pins, withoutLocation }
}

/** Bounds that contain every pin, or null when there is nothing to fit. */
export function pinBounds (pins) {
  if (!Array.isArray(pins) || !pins.length) return null
  let north = -90
  let south = 90
  let east = -180
  let west = 180
  pins.forEach(({ position }) => {
    north = Math.max(north, position.lat)
    south = Math.min(south, position.lat)
    east = Math.max(east, position.lng)
    west = Math.min(west, position.lng)
  })
  return { north, south, east, west }
}

/**
 * Keeps only the latest request alive: next() aborts the previous one and
 * returns { signal, isCurrent }. Responses for an older call are ignored.
 */
export function createLatestGate () {
  let seq = 0
  let controller = null
  return {
    next () {
      if (controller) controller.abort()
      controller = typeof AbortController !== 'undefined' ? new AbortController() : null
      const mine = ++seq
      return {
        signal: controller ? controller.signal : undefined,
        isCurrent: () => mine === seq
      }
    },
    cancel () {
      if (controller) controller.abort()
      controller = null
      seq++
    }
  }
}

/** True for an axios or fetch cancellation, which is not an error to show. */
export function isCancelled (err) {
  if (!err) return false
  return err.name === 'CanceledError' || err.name === 'AbortError' || err.code === 'ERR_CANCELED' || err.__CANCEL__ === true
}
